import type { TFunction } from 'i18next';
import { isApiError, retryAfterSeconds } from './api.js';
import { isSsnError, ssnErrorMessage } from './ssn-messages.js';

/**
 * Traduction des erreurs serveur. Le serveur renvoie un `code` stable ; le front en fait une
 * phrase. Le message technique du serveur n'est jamais affiché tel quel : il est en français en
 * dur et destiné au développeur.
 */

/** Codes connus, déclarés une fois. Un code inconnu retombe sur le message générique. */
const KNOWN_CODES = new Set([
  'account_inactive',
  'ai_not_configured',
  'ai_rate_limited',
  'ai_refused',
  'ai_unavailable',
  'already_active',
  'already_inactive',
  'already_member',
  'already_suspended',
  'appointment_completed',
  'appointment_overlap',
  'agenda_note_title_required',
  'agenda_note_already',
  'agenda_note_not_a_note',
  'already_processed',
  'appointment_patient_mismatch',
  'appointment_source_doctena',
  'appointment_status_external_read_only',
  'appointment_status_provider_cancelled',
  'billing_document_category_locked',
  'birthdate_mismatch',
  'birthdate_verification_locked',
  'birthdate_verification_required',
  'birthday_email_already_sent',
  'birthday_not_upcoming',
  'body_diagram_too_large',
  'buckaroo_store_not_enabled',
  'buckaroo_unauthorized',
  'buckaroo_unavailable',
  'buckaroo_webhook_rejected',
  'buckaroo_webhook_requires_https',
  'cannot_deactivate_self',
  'cannot_impersonate_platform_staff',
  'cannot_impersonate_self',
  'capture_no_file',
  'capture_token_consumed',
  'capture_token_expired',
  'capture_too_many_files',
  'claim_above_justice_de_paix_limit',
  'confirm_name_mismatch',
  'consultation_reason_category_exists',
  'consultation_reason_category_in_use',
  'consultation_reason_exists',
  'consultation_type_exists',
  'credit_note_not_draft',
  'csrf_header_required',
  'debtor_not_in_luxembourg',
  'doctena_bidirectional_sync',
  'doctena_environment_forbidden',
  'doctena_member_mode_off',
  'doctena_mode_confirmation_required',
  'doctena_mode_mismatch',
  'doctena_no_linked_calendars',
  'doctena_practice_required',
  'doctena_rate_limited',
  'doctena_reload_failed',
  'dunning_disabled',
  'dunning_not_pending',
  'dunning_not_resendable',
  'duplicate_candidates',
  'email_unverified',
  'empty_file',
  'encounter_completed',
  'feature_not_in_plan',
  'incomplete_snapshot',
  'file_too_large',
  'follow_up_answered',
  'forbidden',
  'impersonation_active',
  'impersonation_ended',
  'impersonation_read_only',
  'impersonation_target_unavailable',
  'insufficient_role',
  'integration_credentials_unreadable',
  'integration_disabled',
  'integration_not_configured',
  'internal_error',
  'invalid_answer',
  'invalid_credentials',
  'invalid_refresh_token',
  'invalid_status_transition',
  'invalid_token',
  'invalid_totp',
  'invalid_window',
  'invoice_already_exists',
  'invoice_encounter_patient',
  'invoice_has_no_line',
  'invoice_has_payments',
  'invoice_not_cancellable',
  'invoice_not_draft',
  'invoice_not_last',
  'invoice_not_payable',
  'invoice_pdf_retained',
  'invoice_settled',
  'last_owner',
  'late_interest_not_open',
  'letter_is_draft',
  'letter_not_draft',
  'main_location_required',
  'managed_by_stripe',
  'member_has_replacements',
  'member_terms_locked',
  'member_terms_not_applicable',
  'member_terms_overlap',
  'member_terms_scope_fixed',
  'membership_inactive',
  'no_active_organization',
  'no_stripe_customer',
  'not_a_member',
  'not_a_synced_appointment',
  'not_configured',
  'not_encounter_practitioner',
  'not_found',
  'not_suspended',
  'not_yet_due',
  'organization_suspended',
  'overpayment',
  'owner_required',
  'password_change_required',
  'password_unchanged',
  'patient_declined_emails',
  'patient_has_no_email',
  'patient_quota_reached',
  'pediatric_test_age',
  'payment_duplicate',
  'payment_order_exists',
  'payment_order_transition_refused',
  'platform_role_required',
  'provider_not_implemented',
  'questionnaire_template_exists',
  'rate_limited',
  'recipient_has_no_email',
  'refresh_reuse_detected',
  'sequence_complete',
  'series_occurrence_conflict',
  'series_occurrence_not_deletable',
  'series_pattern_change_unsupported',
  'session_revoked',
  'slot_taken',
  'ssn_taken',
  'stale_token',
  'subscription_already_active',
  'subscription_expired',
  'sync_in_progress',
  'task_limit_reached',
  'ticket_file_too_large',
  'ticket_file_type',
  'ticket_too_many_files',
  'totp_already_enabled',
  'totp_not_enabled',
  'totp_not_initialized',
  'totp_required',
  'unauthorized',
  'unknown_clinical_test',
  'unknown_criterion',
  'unknown_question',
  'unknown_questionnaire',
  'unsupported_file_type',
  'validation_error',
  'vat_mode_locked',
]);

/**
 * `fetch` lève un `TypeError` quand aucune réponse n'arrive : Chrome « Failed to fetch »,
 * Firefox « NetworkError… », Safari « Load failed ». Une autre exception — plantage du rendu,
 * JSON illisible après un 200 — n'est pas une coupure : « injoignable » mentirait. Un 4xx ou un
 * 500, lui, est une `ApiError` : le serveur a répondu, on traduit son code.
 */
function isNetworkFailure(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('network request failed')
  );
}

export function errorMessage(error: unknown, t: TFunction): string {
  if (!isApiError(error)) {
    return t(isNetworkFailure(error) ? 'common:errors.network' : 'common:errors.unknown');
  }
  const seconds = retryAfterSeconds(error);
  if (seconds !== null) return t('common:errors.rate_limited_delay', { seconds });
  if (error.code === 'ssn_invalid') return ssnInvalidMessage(error.details, t);
  if (error.code === 'validation_error') return validationMessage(error.details, t);
  return codeMessage(error.code, t);
}

/**
 * Un code stable reçu hors d'une réponse en erreur : motif d'un saut dans un compte rendu groupé,
 * raison pour laquelle une étape est bloquée. Même table que les erreurs HTTP.
 */
export function codeMessage(code: string, t: TFunction): string {
  return KNOWN_CODES.has(code) ? t(`common:errors.${code}`) : t('common:errors.unknown');
}

/**
 * `422 ssn_invalid` : le serveur dit **pourquoi** dans `details.error`, avec les codes de
 * `validateSsn` — les mêmes que le champ traduit en direct, donc la même phrase. Sans code
 * reconnaissable, la phrase générale.
 */
/**
 * `422 validation_error` : l'arbre zod nomme les champs (`properties`), sans leur valeur — une
 * adresse ou un matricule n'a rien à faire dans la phrase. Sans champ identifiable, la phrase
 * générale reste.
 */
function validationMessage(details: unknown, t: TFunction): string {
  const labels = invalidFieldLabels(details, t);
  if (labels.length === 0) return t('common:errors.validation_error');
  return t('common:errors.validation_error_field', { fields: labels.join(', ') });
}

function invalidFieldLabels(details: unknown, t: TFunction): string[] {
  if (typeof details !== 'object' || details === null) return [];
  const properties = (details as { properties?: unknown }).properties;
  if (typeof properties !== 'object' || properties === null) return [];
  const labels: string[] = [];
  for (const field of Object.keys(properties)) {
    const key = `patients:registration.field_${field}`;
    const label = t(key);
    if (label !== key) labels.push(label);
  }
  return labels;
}

function ssnInvalidMessage(details: unknown, t: TFunction): string {
  const code =
    typeof details === 'object' && details !== null
      ? (details as { error?: unknown }).error
      : undefined;
  if (!isSsnError(code)) return t('common:errors.ssn_invalid');
  return t('common:errors.ssn_invalid_reason', { reason: ssnErrorMessage(code, null, t) });
}
